
import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import PatientTab from './components/patients/PatientTab';
import Login from './components/auth/Login';
import { useData } from './context/DataContext';
import { Activity, Apple, BarChart3, Calendar, MessageCircle, Bell, Settings, Home, User, ShieldCheck, HelpCircle, CheckCircle2, AlertCircle, Loader2, X, ListTodo } from 'lucide-react';

const HomeTab = React.lazy(() => import('./components/home/HomeTab'));
const PlansTab = React.lazy(() => import('./components/plans/PlansTab'));
const AppointmentsTab = React.lazy(() => import('./components/appointments/AppointmentsTab'));
const FinancialTab = React.lazy(() => import('./components/financial/FinancialTab'));
const ReportsTab = React.lazy(() => import('./components/reports/ReportsTab'));
const RemindersTab = React.lazy(() => import('./components/reminders/RemindersTab'));
const AcompanhamentoTab = React.lazy(() => import('./components/acompanhamento/AcompanhamentoTab'));
const SettingsTab = React.lazy(() => import('./components/settings/SettingsTab'));
const AdminTab = React.lazy(() => import('./components/admin/AdminTab'));
const TasksTab = React.lazy(() => import('./components/tasks/TasksTab'));
const HelpTab = React.lazy(() => import('./components/help/HelpTab'));

const App: React.FC = () => {
  const { currentUser, toast, setToast } = useData();
  const [activeTab, setActiveTab] = useState('home');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const tabs = [];
  if (currentUser?.role === 'admin') tabs.push({ id: 'admin', label: 'Admin', icon: ShieldCheck });

  tabs.push(
    { id: 'home', label: 'Início', icon: Home },
    { id: 'patients', label: 'Pacientes', icon: User },
    { id: 'plans', label: 'Planos', icon: Apple },
    { id: 'appointments', label: 'Agenda', icon: Calendar },
    { id: 'reminders', label: 'Lembretes', icon: Bell },
    { id: 'tasks', label: 'Minhas Tarefas', icon: ListTodo },
    { id: 'acompanhamento', label: 'Mensagens', icon: MessageCircle },
    { id: 'financial', label: 'Financeiro', icon: BarChart3 },
    { id: 'reports', label: 'Relatórios', icon: Activity },
    { id: 'settings', label: 'Ajustes', icon: Settings },
    { id: 'help', label: 'Ajuda', icon: HelpCircle },
  );

  useEffect(() => {
    if (currentUser?.role === 'admin') setActiveTab('admin');
    else setActiveTab('home');
  }, [currentUser]);

  if (!currentUser) return <Login />;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'admin': return <AdminTab />;
      case 'home': return <HomeTab onNavigate={setActiveTab} />;
      case 'patients': return <PatientTab />;
      case 'plans': return <PlansTab />;
      case 'appointments': return <AppointmentsTab />;
      case 'financial': return <FinancialTab />;
      case 'reminders': return <RemindersTab />;
      case 'tasks': return <TasksTab />;
      case 'acompanhamento': return <AcompanhamentoTab />;
      case 'reports': return <ReportsTab />;
      case 'settings': return <SettingsTab />;
      case 'help': return <HelpTab />;
      default: return <HomeTab />;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800">
      <Sidebar tabs={tabs} activeTab={activeTab} setActiveTab={(id) => { setActiveTab(id); if (window.innerWidth < 768) setIsSidebarOpen(false); }} isOpen={isSidebarOpen} />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <Header onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        
        {/* Global Cloud Sync Toast Notification */}
        {toast.type && (
          <div className={`fixed top-24 right-4 sm:right-8 z-50 flex items-center gap-3 p-4 rounded-2xl shadow-2xl border animate-in slide-in-from-right-8 duration-300 ${
            toast.type === 'success' ? 'bg-green-600 border-green-500 text-white' : 
            toast.type === 'error' ? 'bg-red-600 border-red-500 text-white' : 
            'bg-indigo-600 border-indigo-500 text-white'
          }`}>
            {toast.type === 'success' ? <CheckCircle2 size={20}/> : 
             toast.type === 'error' ? <AlertCircle size={20}/> : 
             <Loader2 size={20} className="animate-spin"/>}
            <span className="text-sm font-bold pr-2">{toast.message}</span>
            <button onClick={() => setToast({message: '', type: null})} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
              <X size={16}/>
            </button>
          </div>
        )}

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="container mx-auto">
            <React.Suspense fallback={<div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div></div>}>
              {renderTabContent()}
            </React.Suspense>
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;
